import type { MelodyTrainerStats, PracticeLevel } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './melody-trainer-summary.module.css'

/**
 * End-of-session summary: how close the notes were.
 *
 * It used to close with which way the session leaned, and that line is gone. Timing is now
 * measured from each phrase's own mean, so a per-attempt mean onset error is identically zero and
 * an average of those zeroes would report "even" to every player on every device - a claim about
 * the clock from a tool that cannot see one (Bowie, 2026-09-13).
 *
 * Attempts nobody performed are excluded from the mean rather than counted as zero, and when that
 * happened the count is shown: a percentage of three attempts is not a percentage of five, and
 * quietly presenting it as one would flatter a broken microphone.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6, §10
 */

export interface MelodyTrainerSummaryProps {
  stats: MelodyTrainerStats
  /** The level this session ran at. Absent for a host that does not run levels. */
  level?: PracticeLevel
  onRestart: () => void
  /**
   * Re-run the same melody at Level 2. Offered only after a Level 1 session, because that
   * progression IS the exercise (§12.1): sing it with the melody, then sing it from memory.
   */
  onTryFromMemory?: () => void
  /** Present when the trainer is a dialog over a melody rather than a page. */
  onClose?: () => void
}

export const MelodyTrainerSummary: FC<MelodyTrainerSummaryProps> = props => {
  const { stats, level, onRestart, onTryFromMemory, onClose } = props
  /* The level's own lexicon entry, so the summary names the exercise in the words that offered it. */
  const levelCopy = useVocalCopy(level === undefined ? '' : `vocal-level-${level}`)

  return (
    <section className={styles.summary} aria-label="Session complete">
      <h2 className={styles.title}>Session complete</h2>
      <p className={styles.score}>{`${stats.meanPercent}%`}</p>

      {stats.scored < stats.total ? (
        <p className={styles.scored}>
          {`Scored on ${stats.scored} of ${stats.total} — the rest were not heard.`}
        </p>
      ) : null}

      {levelCopy ? (
        <p className={styles.level}>
          <TheoryGlyphs>{levelCopy.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.actions}>
        {level === 'sing-along' && onTryFromMemory ? (
          <Button variant="contained" color="primary" size="large" onClick={onTryFromMemory}>
            Now try it from memory
          </Button>
        ) : null}
        <Button
          variant={level === 'sing-along' && onTryFromMemory ? 'outlined' : 'contained'}
          color="primary"
          size="large"
          onClick={onRestart}
        >
          Practise again
        </Button>
        {onClose ? (
          <Button variant="outlined" size="large" onClick={onClose}>
            Done
          </Button>
        ) : null}
      </div>
    </section>
  )
}
